import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  CircularProgress,
  Alert,
  IconButton,
  InputAdornment,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Search as SearchIcon,
  Description as DescriptionIcon,
  Visibility as ViewIcon,
  FileDownload as DownloadIcon,
  ExpandMore as ExpandMoreIcon,
  Article as ArticleIcon,
  Code as CodeIcon,
  Brush as BrushIcon,
  Architecture as ArchitectureIcon,
  Security as SecurityIcon,
  BugReport as TestIcon,
  AccountTree as FlowIcon,
  People as PeopleIcon,
  Storage as TechIcon,
  Api as ApiIcon
} from '@mui/icons-material';
import { PageContainer } from '../components/common';
import { PageTitleContainer } from '../components/common';
import useProject from '../hooks/useProject';
import useSection from '../hooks/useSection';
import documentService from '../services/documentService';
import { getTaskIdForDocumentType } from '../utils/documentMapping';

/**
 * Documents Page
 * Displays all generated documents for a project
 * Layout: Section headers > Task accordions > Document cards grid
 */
const Documents = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();

  const { project, isLoadingProject, projectError } = useProject({ projectId });
  const { sections, isLoadingSections, sectionsError } = useSection({ activeOnly: true });

  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Load documents on mount
  useEffect(() => {
    const loadDocuments = async () => {
      try {
        setLoading(true);
        setError(null);
        const documentData = await documentService.getProjectDocuments(projectId);
        setDocuments(documentData || []);
      } catch (err) {
        console.error('Error loading documents:', err);
        setError(err.message || 'Failed to load documents');
      } finally {
        setLoading(false);
      }
    };
    loadDocuments();
  }, [projectId]);

  // Filter documents based on search query
  const filteredDocuments = documents.filter(doc =>
    doc.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Get task string ID for a document
  const getDocTaskId = (doc) => {
    const docTask = doc.taskId || doc.task || doc.task_id || '';
    if (docTask) return docTask;
    const docType = doc.document_type || doc.documentType || '';
    return getTaskIdForDocumentType(docType) || '';
  };

  // Get documents matching a task
  const getDocumentsByTask = (taskMongoId, taskStringId) => {
    return filteredDocuments.filter(doc => {
      const docTask = getDocTaskId(doc);
      return docTask === taskMongoId || docTask === taskStringId;
    });
  };

  // Check if a document is categorized under any task
  const isDocumentCategorized = (doc) => !!getDocTaskId(doc);

  // Document type helpers
  const getDocTypeLabel = (doc) => {
    const docType = doc.document_type || doc.documentType || doc.type || '';
    const labels = {
      'FEATURES': 'Features', 'USER_STORIES': 'User Stories', 'USER_ROLES': 'User Roles',
      'PRD': 'PRD', 'APD': 'APD', 'WIREFRAMES': 'Wireframes', 'DESIGNS': 'Designs',
      'DESIGN_SYSTEM': 'Design System', 'DESIGN_GUIDELINES': 'Design Guidelines',
      'ARCHITECTURE': 'Architecture', 'TECH_STACK': 'Tech Stack',
      'DEV_GUIDELINES': 'Dev Guidelines', 'API_GUIDELINES': 'API Guidelines',
      'SECURITY_GUIDELINES': 'Security', 'PROCESS_FLOWS': 'Process Flows',
      'FIELD_VALIDATION': 'Field Validations', 'UNIT_TESTING': 'Unit Testing',
      'FINAL_PRD': 'Final PRD', 'MARKDOWN_FILE': 'Markdown'
    };
    return labels[docType] || docType.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'Document';
  };

  const getDocTypeColor = (doc) => {
    const docType = doc.document_type || doc.documentType || doc.type || '';
    const colors = {
      'FEATURES': { bg: '#e3f2fd', text: '#1565c0' },
      'USER_STORIES': { bg: '#e8f5e9', text: '#2e7d32' },
      'USER_ROLES': { bg: '#fce4ec', text: '#c62828' },
      'PRD': { bg: '#ede7f6', text: '#4527a0' }, 'APD': { bg: '#ede7f6', text: '#4527a0' },
      'WIREFRAMES': { bg: '#e0f2f1', text: '#00695c' }, 'DESIGNS': { bg: '#e0f2f1', text: '#00695c' },
      'DESIGN_SYSTEM': { bg: '#e0f2f1', text: '#00695c' }, 'DESIGN_GUIDELINES': { bg: '#e0f2f1', text: '#00695c' },
      'ARCHITECTURE': { bg: '#fff3e0', text: '#e65100' }, 'TECH_STACK': { bg: '#fff3e0', text: '#e65100' },
      'DEV_GUIDELINES': { bg: '#fff8e1', text: '#f57f17' },
      'API_GUIDELINES': { bg: '#e1f5fe', text: '#0277bd' },
      'SECURITY_GUIDELINES': { bg: '#fbe9e7', text: '#bf360c' },
      'PROCESS_FLOWS': { bg: '#f3e5f5', text: '#6a1b9a' },
      'UNIT_TESTING': { bg: '#efebe9', text: '#4e342e' },
    };
    return colors[docType] || { bg: '#f5f5f5', text: '#616161' };
  };

  const getDocIcon = (doc) => {
    const docType = doc.document_type || doc.documentType || doc.type || '';
    const iconSx = { fontSize: 22 };
    switch (docType) {
      case 'FEATURES': case 'USER_STORIES': return <ArticleIcon sx={{ ...iconSx, color: '#1565c0' }} />;
      case 'USER_ROLES': return <PeopleIcon sx={{ ...iconSx, color: '#c62828' }} />;
      case 'WIREFRAMES': case 'DESIGNS': case 'DESIGN_SYSTEM': case 'DESIGN_GUIDELINES':
        return <BrushIcon sx={{ ...iconSx, color: '#00695c' }} />;
      case 'ARCHITECTURE': return <ArchitectureIcon sx={{ ...iconSx, color: '#e65100' }} />;
      case 'TECH_STACK': return <TechIcon sx={{ ...iconSx, color: '#e65100' }} />;
      case 'DEV_GUIDELINES': return <CodeIcon sx={{ ...iconSx, color: '#f57f17' }} />;
      case 'API_GUIDELINES': return <ApiIcon sx={{ ...iconSx, color: '#0277bd' }} />;
      case 'SECURITY_GUIDELINES': return <SecurityIcon sx={{ ...iconSx, color: '#bf360c' }} />;
      case 'PROCESS_FLOWS': return <FlowIcon sx={{ ...iconSx, color: '#6a1b9a' }} />;
      case 'UNIT_TESTING': return <TestIcon sx={{ ...iconSx, color: '#4e342e' }} />;
      case 'PRD': case 'APD': case 'FINAL_PRD': return <DescriptionIcon sx={{ ...iconSx, color: '#4527a0' }} />;
      default: return <DescriptionIcon sx={{ ...iconSx, color: '#757575' }} />;
    }
  };

  const formatDate = (doc) => {
    const dateValue = doc.createdAt || doc.created_at;
    if (!dateValue) return 'Unknown';
    const date = new Date(dateValue);
    return isNaN(date.getTime()) ? 'Unknown' : date.toLocaleDateString('en-US', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const handleDocumentClick = (documentId) => {
    navigate(`/projects/${projectId}/documents/${documentId}`);
  };

  // Render a document card
  const renderDocumentCard = (document) => {
    const typeColor = getDocTypeColor(document);

    return (
      <Box
        key={document._id || document.id}
        sx={{
          border: '1px solid #e2e8f0',
          borderRadius: '8px',
          p: 2.5,
          transition: 'all 0.2s',
          '&:hover': {
            borderColor: '#cbd5e1',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
          }
        }}
      >
        {/* Top row: Created by Name on Date + Actions */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Typography variant="body2" sx={{ color: '#64748b', fontStyle: 'italic', fontSize: '13px' }}>
            Created by {document.created_by_name || 'Unknown'} on {formatDate(document)}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
            <Tooltip title="View">
              <IconButton size="small" onClick={() => handleDocumentClick(document._id || document.id)}
                sx={{ color: '#64748b', '&:hover': { color: '#1e293b' } }}>
                <ViewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {document.url && (
              <Tooltip title="Download">
                <IconButton size="small" component="a" href={document.url} target="_blank" rel="noopener noreferrer"
                  sx={{ color: '#64748b', '&:hover': { color: '#1976d2' } }}>
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        {/* Type */}
        <Typography variant="body2" sx={{ color: '#475569', fontSize: '13px', mb: 1.5 }}>
          <Box component="span" sx={{ color: '#94a3b8' }}>Type:</Box>
          {'  '}{getDocTypeLabel(document)}
        </Typography>

        {/* Bottom link row: icon + document name */}
        <Box
          sx={{
            display: 'flex', alignItems: 'center', gap: 1, pt: 1.5,
            borderTop: '1px solid #f1f5f9', cursor: 'pointer',
            '&:hover .doc-link-text': { color: '#1565c0' }
          }}
          onClick={() => handleDocumentClick(document._id || document.id)}
        >
          <Box sx={{
            width: 28, height: 28, borderRadius: '6px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: typeColor.bg, flexShrink: 0
          }}>
            {getDocIcon(document)}
          </Box>
          <Typography className="doc-link-text" variant="body2" sx={{
            color: '#1976d2', fontSize: '13px',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            transition: 'color 0.2s'
          }}>
            {document.title}
          </Typography>
        </Box>
      </Box>
    );
  };

  // Render loading state
  if (loading || isLoadingProject || isLoadingSections) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Render error state
  if (error || projectError || sectionsError) {
    return (
      <Alert severity="error" sx={{ mt: 4 }}>
        {error || projectError || sectionsError}
      </Alert>
    );
  }

  const uncategorizedDocs = filteredDocuments.filter(doc => !isDocumentCategorized(doc));

  return (
    <PageContainer
      fullWidth
      sx={{
        px: 3, py: 3,
        display: 'flex', flexDirection: 'column',
        height: '100vh', overflow: 'hidden',
        '& .MuiContainer-root': { maxWidth: '100% !important', padding: '0 !important' }
      }}
    >
      {/* Header */}
      <PageTitleContainer
        title="Project generated documents"
        sx={{
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
          ml: 0, mr: 0, mb: 2, mt: 0, py: '16px'
        }}
      />

      {/* Main Content */}
      <Box sx={{
        flex: 1, overflow: 'auto', bgcolor: 'white',
        borderRadius: '4px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        display: 'flex', flexDirection: 'column',
        '&::-webkit-scrollbar': { width: '8px', backgroundColor: 'transparent' },
        '&::-webkit-scrollbar-track': { borderRadius: '10px', backgroundColor: 'rgba(0,0,0,0.05)' },
        '&::-webkit-scrollbar-thumb': { borderRadius: '10px', backgroundColor: 'rgba(0,0,0,0.2)', '&:hover': { backgroundColor: 'rgba(25,118,210,0.4)' } }
      }}>
        <Box sx={{ p: 3 }}>
          {/* Toolbar: Search */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mb: 3, pb: 1.5 }}>
            <TextField
              placeholder="Search documents..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ ml: '10px' }}>
                    <SearchIcon sx={{ color: '#a2abb3', fontSize: 20 }} />
                  </InputAdornment>
                )
              }}
              sx={{
                width: 250,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '4px', color: 'var(--color-blue, #475969)', height: '40px', transition: 'all 0.2s',
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#475969', borderWidth: '1px' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#475969', borderWidth: '1px' }
                },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#4759694d', border: '1px solid #4759694d', borderRadius: '4px' },
                '& .MuiInputBase-input': { fontSize: '14px', padding: '0px !important', color: 'var(--color-blue, #475969)' },
                '& .MuiInputBase-input::placeholder': { color: '#a2abb3', opacity: 0.7, fontSize: '14px' }
              }}
            />
          </Box>

          {/* Scrollable content */}
          <Box sx={{ height: 'calc(100vh - 300px)', overflow: 'auto' }}>
            {filteredDocuments.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <DescriptionIcon sx={{ fontSize: 48, color: '#e0e0e0', mb: 2 }} />
                <Typography variant="h6" sx={{ color: '#94a3b8', fontWeight: 500 }}>
                  {documents.length === 0 ? 'No documents generated yet' : 'No documents match your search'}
                </Typography>
                <Typography variant="body2" sx={{ color: '#cbd5e1', mt: 0.5 }}>
                  {documents.length === 0 ? 'Save content from your conversations to see them here' : 'Try adjusting your search'}
                </Typography>
              </Box>
            ) : (
              <Box>
                {/* Render sections */}
                {sections.map(section => {
                  // Get tasks that have documents
                  const tasksWithDocs = (section.tasks || []).filter(task =>
                    getDocumentsByTask(task._id, task.id).length > 0
                  );
                  if (tasksWithDocs.length === 0) return null;

                  return (
                    <Box key={section._id} sx={{ mb: 3 }}>
                      {/* Section Header - plain text label */}
                      <Typography variant="subtitle1" sx={{
                        fontWeight: 600, color: '#1e293b', fontSize: '16px',
                        mb: 1.5, pl: 0.5
                      }}>
                        {section.name}
                      </Typography>

                      {/* Task Accordions */}
                      {tasksWithDocs.map(task => {
                        const taskDocs = getDocumentsByTask(task._id, task.id);
                        return (
                          <Accordion
                            key={task._id || task.id}
                            defaultExpanded
                            sx={{
                              mb: 1.5,
                              boxShadow: 'none',
                              border: '1px solid #e2e8f0',
                              borderRadius: '8px !important',
                              '&:before': { display: 'none' },
                              overflow: 'hidden'
                            }}
                          >
                            <AccordionSummary
                              expandIcon={<ExpandMoreIcon sx={{ color: '#64748b' }} />}
                              sx={{
                                bgcolor: '#f8fafc',
                                minHeight: 48,
                                '&.Mui-expanded': { minHeight: 48 },
                                '& .MuiAccordionSummary-content': { my: 1 }
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#334155', fontSize: '14px' }}>
                                  {task.name || task.title}
                                </Typography>
                                <Box sx={{
                                  ml: 1.5, px: 1, py: 0.1, borderRadius: '10px',
                                  bgcolor: '#edf2f7', fontSize: '11px', color: '#64748b', fontWeight: 600
                                }}>
                                  {taskDocs.length}
                                </Box>
                              </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ p: 2, bgcolor: 'white' }}>
                              <Box sx={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
                                gap: 2
                              }}>
                                {taskDocs.map(doc => renderDocumentCard(doc))}
                              </Box>
                            </AccordionDetails>
                          </Accordion>
                        );
                      })}
                    </Box>
                  );
                })}

                {/* Uncategorized documents */}
                {uncategorizedDocs.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" sx={{
                      fontWeight: 600, color: '#1e293b', fontSize: '16px',
                      mb: 1.5, pl: 0.5
                    }}>
                      Other Documents
                    </Typography>
                    <Accordion
                      defaultExpanded
                      sx={{
                        mb: 1.5,
                        boxShadow: 'none',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px !important',
                        '&:before': { display: 'none' },
                        overflow: 'hidden'
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon sx={{ color: '#64748b' }} />}
                        sx={{
                          bgcolor: '#f8fafc',
                          minHeight: 48,
                          '&.Mui-expanded': { minHeight: 48 },
                          '& .MuiAccordionSummary-content': { my: 1 }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#334155', fontSize: '14px' }}>
                            Uncategorized
                          </Typography>
                          <Box sx={{
                            ml: 1.5, px: 1, py: 0.1, borderRadius: '10px',
                            bgcolor: '#edf2f7', fontSize: '11px', color: '#64748b', fontWeight: 600
                          }}>
                            {uncategorizedDocs.length}
                          </Box>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ p: 2, bgcolor: 'white' }}>
                        <Box sx={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
                          gap: 2
                        }}>
                          {uncategorizedDocs.map(doc => renderDocumentCard(doc))}
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </PageContainer>
  );
};

export default Documents;
